/**
 * useJournalArticlesPage
 *
 * The single hook every table-shaped journal articles page calls. It wires a
 * page into the shared JournalArticlesContext (the only source of fetching and
 * caching) and owns the two pieces of per-page plumbing that used to live
 * inside the monolithic JournalArticlesInterface:
 *
 *   1. URL <-> context state synchronisation (seed from query params on mount,
 *      write state back to the URL as it changes).
 *   2. Fetch orchestration: a single idempotent effect that drives
 *      `context.fetchPage` whenever the real query inputs change, with a
 *      signature guard so returning from the article detail page (which already
 *      holds matching cached data) does not trigger a reload.
 *
 * Pages do NOT re-implement any of this. They call the hook once, passing the
 * fixed sidebar option for that page, and compose the returned state/actions
 * into whatever layout they need (shell + toolbar + pagination + table, or
 * something entirely different).
 *
 * The hook deliberately returns the whole context value plus a small set of
 * derived values and pagination handlers, so a page can pass the single
 * returned object to the shared building blocks rather than threading dozens of
 * individual props.
 */

import { useCallback, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import {
  useJournalArticles,
  JournalArticlesContextValue,
  JournalArticleSortableKey,
  PrBulkReviewFilter,
  PR_BULK_REVIEW_SCORE_THRESHOLD,
} from "@/components/journal-articles/JournalArticlesContext"
import { SidebarOption } from "@/components/journal-articles/JournalArticlesSidebar"
import { GenericSortConfig } from "@/components/survey-catalogue/SortableColumnHeader"
import { ViewMode } from "@/components/shared/TableViewSelector"
import { JournalArticle } from "@/services/journalArticlesService"
import { getColumnGroupsForViewMode } from "@/components/journal-articles/columns"

/** Available page size options offered in the Rows selector. */
export const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100, 200]

/** Default page size (must match the provider default in JournalArticlesContext). */
export const DEFAULT_PAGE_SIZE: number = 50

/** Upper bound of the PR Bulk Review minimum-score slider. */
export const PR_SCORE_SLIDER_MAX: number = 1

/** Step increment of the PR Bulk Review minimum-score slider. */
export const PR_SCORE_SLIDER_STEP: number = 0.05

/** View modes that are valid to seed from / write to the URL `view` param. */
const VALID_VIEW_MODES: ViewMode[] = ["compact", "status", "full"]

/**
 * Options accepted by {@link useJournalArticlesPage}.
 */
export interface UseJournalArticlesPageOptions {
  /**
   * The sidebar option this page represents. Fixing it here is what makes the
   * page "be" a particular view (e.g. "open-author-contacts"). The hook pushes
   * this into the context on mount and whenever it changes, so the context's
   * `fetchPage` builds the correct topic/collection/status filters.
   */
  sidebarOption: SidebarOption
  /**
   * When set, the page locks the table to a single view mode (e.g. PR Bulk
   * Review's dedicated "pr-bulk-review" layout). The hook forces this mode into
   * the context and seeds the matching default visible columns. When omitted,
   * the user's density choice is honoured and seeded from the URL as usual.
   */
  lockedViewMode?: ViewMode
  /**
   * The sort to apply on first mount when the user has not already chosen one
   * (no sort is set in context and none was seeded from the URL). Used by the
   * PR Bulk Review page to default to score-descending order, which is both the
   * most useful review order and the order its backing index (the sparse
   * entity-scorePRTopic-index) reads natively — so the initial load takes the
   * fast score-ordered query path. The user can still re-sort afterwards; this
   * only sets the initial order.
   */
  defaultSort?: GenericSortConfig<JournalArticleSortableKey>
}

/**
 * The value returned by {@link useJournalArticlesPage}: the full context value
 * plus page-level derived values and pagination handlers.
 */
export interface JournalArticlesPage extends JournalArticlesContextValue {
  /**
   * The lower bound of the PR Bulk Review score slider for the active
   * quick-filter tab. "Needs Rereview" and "New Finds" always pin
   * scorePRTopic > 0.5, so the slider cannot drop below 0.5 on those tabs;
   * "All" has no inherent floor (0). Only meaningful on the PR Bulk Review page.
   */
  prTabScoreFloor: number
  /** Go to the next page of results (no-op on the last page). */
  goToNextPage: () => void
  /** Go to the previous page of results (no-op on the first page). */
  goToPreviousPage: () => void
  /** Go to the first page (no-op if already there). */
  goToFirstPage: () => void
  /** Go to the last page (no-op if already there). */
  goToLastPage: () => void
  /**
   * Change the number of rows per page and reset to the first page. Ignores
   * values outside {@link PAGE_SIZE_OPTIONS}.
   *
   * @param value - The selected page size as a string (from a Select control).
   */
  changePageSize: (value: string) => void
  /**
   * Apply a new filter map and clear any open details panel.
   *
   * @param filters - The new active filter map.
   */
  changeFilters: (filters: Map<string, Set<string>>) => void
}

/**
 * Wire a journal articles page into the shared context, syncing URL state and
 * orchestrating fetches.
 *
 * @param options - The page's fixed sidebar option and optional locked view mode.
 * @returns The context value augmented with derived values and page handlers.
 */
export function useJournalArticlesPage(
  options: UseJournalArticlesPageOptions
): JournalArticlesPage {
  const { sidebarOption, lockedViewMode, defaultSort } = options
  const [searchParams, setSearchParams] = useSearchParams()
  const context = useJournalArticles()

  const {
    articles,
    totalPages,
    pageIndex,
    pageSize,
    sidebarOption: contextSidebarOption,
    prBulkReviewFilter,
    prScoreMin,
    searchQuery,
    activeFilters,
    sortConfig,
    viewMode,
    visibleColumns,
    lastFetchTime,
    fetchPage,
    setPageIndex,
    setPageSize,
    setSidebarOption,
    setPrBulkReviewFilter,
    setPrScoreMin,
    setSearchQuery,
    setActiveFilters,
    setSortConfig,
    setViewMode,
    setVisibleColumns,
    clearSelectedArticle,
  } = context

  // The lower bound of the minimum-score slider for the active quick-filter
  // tab (see JournalArticlesPage.prTabScoreFloor).
  const prTabScoreFloor: number =
    prBulkReviewFilter === "needs-rereview" || prBulkReviewFilter === "new-finds"
      ? PR_BULK_REVIEW_SCORE_THRESHOLD
      : 0

  // Guards so URL params are only applied to context once, on initial mount.
  const initialLoadDoneRef = useRef<boolean>(false)
  const prevSidebarOptionRef = useRef<SidebarOption>(sidebarOption)

  // ---------------------------------------------------------------------------
  // Keep the context's sidebar option aligned with this page's fixed option.
  //
  // On mount this seeds the option before the first fetch; after mount it fires
  // only if the page's option changes (e.g. the TopicPage switching between
  // topic slugs, or a collection page remounting for a different id). Mirrors
  // the two effects the old interface used to derive the option from the URL,
  // but here the page states its option directly.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      return
    }
    if (sidebarOption !== prevSidebarOptionRef.current) {
      prevSidebarOptionRef.current = sidebarOption
      setSidebarOption(sidebarOption)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOption])

  // ---------------------------------------------------------------------------
  // Lock the view mode when the page requires it (PR Bulk Review). Forces the
  // context view mode and seeds the matching default visible columns whenever
  // the lock is active and not already applied.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (lockedViewMode && viewMode !== lockedViewMode) {
      setViewMode(lockedViewMode)
      setVisibleColumns(
        new Set(getColumnGroupsForViewMode(lockedViewMode).flatMap((g) => g.keys))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedViewMode])

  // ---------------------------------------------------------------------------
  // Safety net: keep the stored slider minimum at or above the active tab's
  // floor. Switching tabs already resets the slider to the new tab's floor (in
  // setPrBulkReviewFilter); this only guards the URL-seed edge case (a stale
  // link carrying prFilter=new-finds with a prScoreMin below 0.5).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (lockedViewMode === "pr-bulk-review" && prScoreMin < prTabScoreFloor) {
      setPrScoreMin(prTabScoreFloor)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prTabScoreFloor])

  // ---------------------------------------------------------------------------
  // Initial mount: seed context state from URL query params, then let the fetch
  // effect below drive the first fetch.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (initialLoadDoneRef.current) {
      return
    }
    initialLoadDoneRef.current = true

    // Seed the sidebar option before the first fetch so the fetch uses the
    // correct topic/collection filter.
    if (sidebarOption !== contextSidebarOption) {
      setSidebarOption(sidebarOption)
    }
    prevSidebarOptionRef.current = sidebarOption

    // If the context already holds fetched data (lastFetchTime set), this is a
    // REMOUNT — e.g. returning from the article detail page. Re-seeding from the
    // URL would install fresh Map/string references and trigger a reload. Skip
    // seeding entirely and let the cached data stand.
    if (lastFetchTime !== null) {
      return
    }

    // Page size.
    const urlPageSize = searchParams.get("pageSize")
    if (urlPageSize) {
      const parsed = parseInt(urlPageSize, 10)
      if (PAGE_SIZE_OPTIONS.includes(parsed) && parsed !== pageSize) {
        setPageSize(parsed)
      }
    }

    // View mode (ignored when the page locks the mode).
    if (!lockedViewMode) {
      const urlView = searchParams.get("view")
      if (urlView && VALID_VIEW_MODES.includes(urlView as ViewMode) && urlView !== viewMode) {
        setViewMode(urlView as ViewMode)
      }
    }

    // Search.
    const urlSearch = searchParams.get("search")
    if (urlSearch && urlSearch !== searchQuery) {
      setSearchQuery(urlSearch)
    }

    // Default sort (e.g. PR Bulk Review defaulting to score-descending). Only
    // applied on a genuine first load when the user has no sort set, so it
    // never overrides a sort the user chose. Sort is not persisted to the URL,
    // so there is no URL seed to reconcile against — the context default is
    // null, and this replaces that null with the page's preferred initial sort.
    if (defaultSort && sortConfig === null) {
      setSortConfig(defaultSort)
    }

    // PR Bulk Review quick-filter (only meaningful on that page).
    const urlPrFilter = searchParams.get("prFilter")
    const validPrFilters: PrBulkReviewFilter[] = ["all", "needs-rereview", "new-finds"]
    if (
      urlPrFilter &&
      validPrFilters.includes(urlPrFilter as PrBulkReviewFilter) &&
      urlPrFilter !== prBulkReviewFilter
    ) {
      setPrBulkReviewFilter(urlPrFilter as PrBulkReviewFilter)
    }

    // PR Bulk Review minimum-score slider (only meaningful on that page). Only
    // finite numbers in the valid [0, max] range are honoured.
    const urlPrScoreMin = searchParams.get("prScoreMin")
    if (urlPrScoreMin !== null) {
      const parsed: number = Number(urlPrScoreMin)
      if (
        Number.isFinite(parsed) &&
        parsed >= 0 &&
        parsed <= PR_SCORE_SLIDER_MAX &&
        parsed !== prScoreMin
      ) {
        setPrScoreMin(parsed)
      }
    }

    // Visible columns (validated against the active view mode's column groups).
    const urlColumns = searchParams.get("columns")
    if (urlColumns) {
      const currentColumnGroups = getColumnGroupsForViewMode(
        lockedViewMode ?? (searchParams.get("view") as ViewMode) ?? viewMode ?? "compact"
      )
      const validKeySet = new Set(currentColumnGroups.flatMap((g) => g.keys))
      const keys = urlColumns
        .split(",")
        .filter((k) => validKeySet.has(k as keyof JournalArticle))
      if (keys.length > 0) {
        setVisibleColumns(new Set(keys as (keyof JournalArticle)[]))
      }
    }

    // Filters.
    const urlFilters = new Map<string, Set<string>>()
    const filterPrefix = "filter_"
    searchParams.forEach((value, key) => {
      if (key.startsWith(filterPrefix) && value) {
        const filterKey = key.slice(filterPrefix.length)
        urlFilters.set(filterKey, new Set(value.split(",").map(decodeURIComponent)))
      }
    })
    if (urlFilters.size > 0) {
      setActiveFilters(urlFilters)
    }

    // Page index (1-based in URL, 0-based internally).
    const urlPage = searchParams.get("page")
    if (urlPage) {
      const parsed = parseInt(urlPage, 10)
      if (!isNaN(parsed) && parsed >= 1) {
        setPageIndex(parsed - 1)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------------------
  // Fetch orchestration. A single idempotent effect is the only place that
  // triggers a fetch. It watches the ACTUAL context query inputs and uses a
  // signature guard so StrictMode double-invokes and spurious fetchPage identity
  // changes do not cause reloads, and so returning from the detail page with
  // matching cached data does not refetch.
  // ---------------------------------------------------------------------------
  const lastFetchSignatureRef = useRef<string | null>(null)

  const buildFetchSignature = useCallback(
    (page: number): string => {
      const filtersPart = [...activeFilters.entries()]
        .map(([key, values]) => `${key}:${[...values].sort().join("|")}`)
        .sort()
        .join(";")
      const sortPart = sortConfig ? `${sortConfig.key}:${sortConfig.direction}` : "none"
      // The PR Bulk Review quick-filter and minimum-score slider are real query
      // inputs (they change the filters sent to the API in the context's
      // fetchPage), so they MUST be part of the signature. Omitting them means
      // clicking a quick-filter toggle or moving the slider leaves the signature
      // unchanged, the effect's guard short-circuits, and no refetch happens.
      return [
        contextSidebarOption,
        searchQuery,
        pageSize,
        page,
        sortPart,
        filtersPart,
        prBulkReviewFilter,
        prScoreMin,
      ].join("~")
    },
    [
      contextSidebarOption,
      searchQuery,
      pageSize,
      sortConfig,
      activeFilters,
      prBulkReviewFilter,
      prScoreMin,
    ]
  )

  useEffect(() => {
    // Seed the signature from cached context state on the very first run so a
    // return from the detail page (already holding matching data) does not
    // refetch.
    if (lastFetchSignatureRef.current === null) {
      const hasCachedData =
        lastFetchTime !== null &&
        articles.length > 0 &&
        contextSidebarOption === sidebarOption
      if (hasCachedData) {
        lastFetchSignatureRef.current = buildFetchSignature(pageIndex)
        return
      }
    }

    const signature = buildFetchSignature(pageIndex)
    if (signature === lastFetchSignatureRef.current) {
      return
    }

    lastFetchSignatureRef.current = signature
    fetchPage(pageIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contextSidebarOption,
    searchQuery,
    activeFilters,
    sortConfig,
    pageSize,
    pageIndex,
    prBulkReviewFilter,
    prScoreMin,
    buildFetchSignature,
    fetchPage,
  ])

  // ---------------------------------------------------------------------------
  // Sync context state back to the URL query params.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const params: Record<string, string> = {}

    // A locked-mode page implies its mode from the route, so it is not written
    // to the URL; elsewhere a non-default density mode is persisted.
    if (!lockedViewMode && viewMode !== "compact") {
      params.view = viewMode
    }

    // Persist the active PR Bulk Review quick-filter (except the "all" default).
    if (lockedViewMode === "pr-bulk-review" && prBulkReviewFilter !== "all") {
      params.prFilter = prBulkReviewFilter
    }

    // Persist the PR Bulk Review minimum-score slider only when it sits above
    // the active tab's own floor.
    if (lockedViewMode === "pr-bulk-review" && prScoreMin > prTabScoreFloor) {
      params.prScoreMin = String(prScoreMin)
    }

    if (searchQuery) {
      params.search = searchQuery
    }

    const currentColumnGroups = getColumnGroupsForViewMode(viewMode)
    const currentDefaultKeys = currentColumnGroups.flatMap((g) => g.keys)
    const allCurrentVisible = currentDefaultKeys.every((k) => visibleColumns.has(k))
    if (!allCurrentVisible) {
      const relevantVisible = [...visibleColumns].filter((k) =>
        currentDefaultKeys.includes(k as keyof JournalArticle)
      )
      if (relevantVisible.length > 0) {
        params.columns = relevantVisible.join(",")
      }
    }

    if (pageIndex > 0) {
      params.page = String(pageIndex + 1)
    }

    if (pageSize !== DEFAULT_PAGE_SIZE) {
      params.pageSize = String(pageSize)
    }

    activeFilters.forEach((values, key) => {
      if (values.size > 0) {
        params[`filter_${key}`] = [...values].join(",")
      }
    })

    setSearchParams(params, { replace: true })
  }, [
    lockedViewMode,
    viewMode,
    visibleColumns,
    pageIndex,
    pageSize,
    setSearchParams,
    searchQuery,
    activeFilters,
    prBulkReviewFilter,
    prScoreMin,
    prTabScoreFloor,
  ])

  // ---------------------------------------------------------------------------
  // Pagination + input handlers. These mirror the handlers the old interface
  // defined, closing the details panel on navigation so a stale selection does
  // not linger across pages.
  // ---------------------------------------------------------------------------
  const goToNextPage = useCallback((): void => {
    if (pageIndex >= totalPages - 1) return
    setPageIndex(pageIndex + 1)
    clearSelectedArticle()
  }, [pageIndex, totalPages, setPageIndex, clearSelectedArticle])

  const goToPreviousPage = useCallback((): void => {
    if (pageIndex === 0) return
    setPageIndex(pageIndex - 1)
    clearSelectedArticle()
  }, [pageIndex, setPageIndex, clearSelectedArticle])

  const goToFirstPage = useCallback((): void => {
    if (pageIndex === 0) return
    setPageIndex(0)
    clearSelectedArticle()
  }, [pageIndex, setPageIndex, clearSelectedArticle])

  const goToLastPage = useCallback((): void => {
    if (pageIndex >= totalPages - 1) return
    setPageIndex(totalPages - 1)
    clearSelectedArticle()
  }, [pageIndex, totalPages, setPageIndex, clearSelectedArticle])

  const changePageSize = useCallback(
    (value: string): void => {
      const parsed: number = parseInt(value, 10)
      if (!PAGE_SIZE_OPTIONS.includes(parsed)) return
      setPageSize(parsed)
      setPageIndex(0)
      clearSelectedArticle()
    },
    [setPageSize, setPageIndex, clearSelectedArticle]
  )

  const changeFilters = useCallback(
    (filters: Map<string, Set<string>>): void => {
      setActiveFilters(filters)
      clearSelectedArticle()
    },
    [setActiveFilters, clearSelectedArticle]
  )

  return {
    ...context,
    prTabScoreFloor,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    changePageSize,
    changeFilters,
  }
}


/**
 * Columns that are only meaningful on the Open Author Contacts page. On every
 * other page they should be stripped from the visible set so they never render,
 * even if the default column groups or a stale URL `columns` param includes
 * them.
 */
const LOCATION_ONLY_COLUMNS: string[] = ["continents", "countries", "studyYears"]

/**
 * Strip the Open-Author-Contacts-only location columns from a visible-columns
 * set. Every page except Open Author Contacts should pass the result to
 * `JournalArticlesTable` rather than the raw `visibleColumns` from the page
 * hook.
 *
 * @param visibleColumns - The current visible column set from the page hook.
 * @returns A new Set with the location columns removed.
 */
export function stripLocationColumns(visibleColumns: Set<string>): Set<string> {
  const filtered = new Set(visibleColumns)
  for (const key of LOCATION_ONLY_COLUMNS) {
    filtered.delete(key)
  }
  return filtered
}
